"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.sanitizeTransformOp = exports.getSanitizeTransformOpOutputDataTape = exports.getSanitizedConfig = exports.getSanitizedCsvData = exports.getSanitizedRowData = exports.createSanitizedFieldConfigs = exports.getSanitizedDataTapeFieldNoThrow = exports.getSanitizedDataTapeField = exports.getHeaders = exports.validateHeaders = exports.getSanitizedFieldName = void 0;
const csvtojson_1 = __importDefault(require("csvtojson"));
const lodash_1 = __importDefault(require("lodash"));
const remeda_1 = require("remeda");
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const typeguards_1 = require("./typeguards");
const utils_1 = require("./utils");
// excel/csv have a header row and are 1 based arrays, so adjust for our 0 based rowData
const STANDARD_ROW_INDEX_OFFSET = 2;
const getFieldValidationErrorMessage = (invalidFields) => {
    return `Validation Failed: ${invalidFields.length} invalid fields: [${invalidFields.map((field) => field.name).join(', ')}]`;
};
const getSanitizedFieldName = (fieldName, sanitizeFieldNames) => (sanitizeFieldNames ? lodash_1.default.snakeCase(fieldName) : fieldName);
exports.getSanitizedFieldName = getSanitizedFieldName;
const validateHeaders = ({ headers, externalIdField, }) => {
    const errors = [];
    const uniqueHeaders = lodash_1.default.uniq(headers);
    if (uniqueHeaders.length !== headers.length) {
        const duplicateHeaders = (0, remeda_1.pipe)(headers, (0, remeda_1.groupBy)((header) => header), remeda_1.values, (0, remeda_1.filter)((dupeHeaders) => dupeHeaders.length > 1), (0, remeda_1.flatten)(), (0, remeda_1.uniq)());
        errors.push({
            type: 'metadataValidation',
            message: `Duplicate Headers: ["${duplicateHeaders.join('", "')}"]`,
        });
    }
    if (lodash_1.default.isString(externalIdField) && !lodash_1.default.includes(headers, externalIdField)) {
        errors.push({
            type: 'metadataValidation',
            message: `External Id Field does not exist: ${externalIdField}`,
        });
    }
    return errors;
};
exports.validateHeaders = validateHeaders;
/**
 * Reads sanitized headers from first line of csv file.
 *
 * If `sanitizeFieldNames` is true, headers will be
 * sanitized to be snake_cased. Otherwise,
 * headers will read from the CSV as-is.
 * Throws error if externalIdField does not exist in csv.
 */
const getHeaders = async ({ csvString, delimiter, externalIdField, sanitizeFieldNames, }) => {
    let headers = [];
    await (0, csvtojson_1.default)({ delimiter })
        .fromString(csvString.split('\n')[0])
        .on('header', (rawheaders) => {
        headers = rawheaders.map((rawHeader) => (0, exports.getSanitizedFieldName)(rawHeader, sanitizeFieldNames));
    });
    const errors = (0, exports.validateHeaders)({ headers, externalIdField });
    if (errors.length) {
        throw errors;
    }
    return headers;
};
exports.getHeaders = getHeaders;
const throwInvalidField = (invalidField) => {
    throw invalidField;
};
/**
 * Method to sanitize a single field, will throw an InvalidField if it errors
 * @param baseFieldConfig
 * @param value
 * @returns
 */
const getSanitizedDataTapeField = (baseFieldConfig, value, preSanitized = false) => {
    const isRequiredField = (0, typeguards_1.isRequiredBaseFieldConfig)(baseFieldConfig);
    if (!isRequiredField) {
        // for optional fields, user can (optionally) specify list of externalNullValues
        // that they want to be treated as null
        const externalNullValues = baseFieldConfig.externalNullValues || [];
        if (value === undefined || externalNullValues.includes(value)) {
            return null;
        }
    }
    // csvtojson can return an empty string or an object without a column defined, so handle both cases
    // this handles all required fields that are missing and all optional that are missing
    if (lodash_1.default.isNil(value) ||
        common_1.stringConversion.isEmptyString(value) ||
        // @dani we should be able to remove this str null conditional after csv conversion library callsite moves are done
        value === 'null') {
        if ((0, typeguards_1.isRequiredBaseFieldConfig)(baseFieldConfig) &&
            baseFieldConfig.defaultTo !== undefined) {
            (0, common_1.finleyAssert)(!common_1.stringConversion.isEmptyString(baseFieldConfig.defaultTo) &&
                baseFieldConfig.defaultTo !== 'null', 'setting empty string or "null" as defaultTo will cause infinite loop');
            return (0, exports.getSanitizedDataTapeField)(baseFieldConfig, baseFieldConfig.defaultTo);
        }
        if (isRequiredField) {
            throwInvalidField({
                name: baseFieldConfig.field,
                value: 'undefined',
                expectedType: baseFieldConfig.type,
                expectedFormat: (0, utils_1.getExpectedFormat)(baseFieldConfig),
            });
        }
        return null;
    }
    try {
        if ((0, typeguards_1.isBooleanBaseFieldConfig)(baseFieldConfig)) {
            return common_1.stringConversion.convertStringBoolean(value, preSanitized ? 'true' : baseFieldConfig.externalBooleanTrue);
        }
        if ((0, typeguards_1.isCurrencyBaseFieldConfig)(baseFieldConfig)) {
            return common_1.stringConversion.convertStrictCurrencyFactory(baseFieldConfig.currencyCode)(value);
        }
        if ((0, typeguards_1.isDateBaseFieldConfig)(baseFieldConfig)) {
            if (preSanitized) {
                if (!common_1.typeguards.isFullDateStr(value)) {
                    throwInvalidField({
                        name: baseFieldConfig.field,
                        value,
                        expectedType: 'Sanitized FullDateStr',
                        expectedFormat: 'yyyy-LL-dd',
                    });
                }
                return value;
            }
            // For standard ISO 8601 date formats, do not use luxon (fromFormat is much slower)
            if (baseFieldConfig.externalDateFormat === 'yyyy-LL-dd') {
                if (!common_1.typeguards.isISO8601Date(value)) {
                    throwInvalidField({
                        name: baseFieldConfig.field,
                        value,
                        expectedType: 'ISO 8601 date',
                        expectedFormat: 'yyyy-LL-dd',
                    });
                }
                return value;
            }
            // All other formats, fall back to date utility
            return common_1.date.strToFullDateStr(value, baseFieldConfig.externalDateFormat);
        }
        if ((0, typeguards_1.isDateTimeBaseFieldConfig)(baseFieldConfig)) {
            if (preSanitized) {
                if (!common_1.typeguards.isFullDateTimeStr(value)) {
                    throwInvalidField({
                        name: baseFieldConfig.field,
                        value,
                        expectedType: 'Sanitized FullDateTimeStr',
                        expectedFormat: `yyyy-MM-dd'T'HH:mm:ss.SSS'Z'`,
                    });
                }
                return value;
            }
            const dateTime = common_1.date.strToDateTime(value, baseFieldConfig.externalDateTimeFormat, baseFieldConfig.defaultTz);
            return common_1.date.dateTimetoFullDateTimeStr(dateTime);
        }
        if ((0, typeguards_1.isFloatBaseFieldConfig)(baseFieldConfig)) {
            return common_1.stringConversion.convertStrictNumber(value);
        }
        if ((0, typeguards_1.isIntegerBaseFieldConfig)(baseFieldConfig)) {
            return common_1.stringConversion.convertStrictInteger(value);
        }
        if ((0, typeguards_1.isPercentageBaseFieldConfig)(baseFieldConfig)) {
            // don't double sanitize a percent, return the number instead
            if (preSanitized) {
                return common_1.stringConversion.convertStrictNumber(value);
            }
            return common_1.stringConversion.convertStrictPercentageToFraction(value);
        }
        if ((0, typeguards_1.isStringBaseFieldConfig)(baseFieldConfig)) {
            return common_1.stringConversion.convertStrictNonEmptyString(lodash_1.default.trim(value));
        }
    }
    catch (err) {
        if ((0, typeguards_1.isInvalidField)(err)) {
            throw err;
        }
        throwInvalidField({
            name: baseFieldConfig.field,
            value,
            expectedType: baseFieldConfig.type,
            expectedFormat: (0, utils_1.getExpectedFormat)(baseFieldConfig),
        });
    }
    return value;
};
exports.getSanitizedDataTapeField = getSanitizedDataTapeField;
/**
 * Method to sanitize a single field, will return SanitizedFieldResult
 * this method will not throw an error
 */
const getSanitizedDataTapeFieldNoThrow = (baseFieldConfig, value, preSanitized = false) => {
    try {
        return {
            isValid: true,
            data: (0, exports.getSanitizedDataTapeField)(baseFieldConfig, value, preSanitized),
        };
    }
    catch (err) {
        const invalidFieldError = (0, typeguards_1.isInvalidField)(err)
            ? err
            : {
                name: baseFieldConfig.field,
                value: String(value),
                expectedType: baseFieldConfig.type,
                expectedFormat: (0, utils_1.getExpectedFormat)(baseFieldConfig),
            };
        return {
            isValid: false,
            error: invalidFieldError,
        };
    }
};
exports.getSanitizedDataTapeFieldNoThrow = getSanitizedDataTapeFieldNoThrow;
const createSanitizedFieldConfigs = (params) => {
    const { baseFields, sanitizeFieldNames } = params;
    // In `SanitizeTransformation`, base field names are configured with their pre-sanitize naming,
    // i.e. columns are named exactly as customers send to us. getHeaders() above will standardize
    // these namings, so output data uses standardized names too, thus sanitizing config here as well
    return baseFields.map((baseField) => {
        const { field, columnToReadFrom } = baseField;
        const sanitizedField = (0, exports.getSanitizedFieldName)(field, sanitizeFieldNames);
        // Recall that "columnToReadFrom" is an optional attribute that only
        // helps with fetching the data from the CSV (represents the header).
        // Need to rely on "sanitizeFieldNames" since our buffer --> CSV conversion
        // also uses this so we need to ensure we're in the same sanitized state
        const sanitizedColumn = columnToReadFrom
            ? (0, exports.getSanitizedFieldName)(columnToReadFrom, sanitizeFieldNames)
            : undefined;
        return {
            ...baseField,
            field: sanitizedField,
            columnToReadFrom: sanitizedColumn,
        };
    });
};
exports.createSanitizedFieldConfigs = createSanitizedFieldConfigs;
/**
 * Method to sanitize a row of CSV data
 * Throws a LoanTapeValidationError if there are any errors in the data
 * @param row single row of provided CSV data
 * @param rowIndex generally will be index + STANDARD_ROW_INDEX_OFFSET to account for header row
 *   and 1 based array format of csv
 * @param baseFieldConfigs
 * @param externalReferenceIdField
 * @returns single sanitized CSV data row
 */
const getSanitizedRowData = ({ row, rowIndex, baseFieldConfigs, externalReferenceIdField, preSanitized, }) => {
    const invalidFields = [];
    const data = baseFieldConfigs.reduce((acc, baseFieldConfig) => {
        const { field, columnToReadFrom } = baseFieldConfig;
        const fieldResult = (0, exports.getSanitizedDataTapeFieldNoThrow)(baseFieldConfig, row[columnToReadFrom ?? field], preSanitized);
        if (!fieldResult.isValid) {
            invalidFields.push(fieldResult.error);
            return acc;
        }
        // eslint-disable-next-line no-param-reassign
        acc[field] = fieldResult.data;
        return acc;
    }, {});
    if (invalidFields.length === 0) {
        return { isValid: true, data };
    }
    return {
        isValid: false,
        error: {
            type: 'fieldValidation',
            row: rowIndex,
            externalReceivableId: externalReferenceIdField
                ? String(row[externalReferenceIdField])
                : 'unspecified',
            invalidFields,
            message: getFieldValidationErrorMessage(invalidFields),
        },
    };
};
exports.getSanitizedRowData = getSanitizedRowData;
/**
 * Method to sanitize an array of CSV data
 * @param csvArray
 * @param baseFieldConfig
 * @param externalReferenceIdField
 * @returns
 */
const getSanitizedCsvData = ({ csvArray, baseFieldConfigs, externalReferenceIdField, preSanitized = false, markSanitized = true, }) => {
    const fieldValidationErrors = [];
    const data = csvArray.map((row, rowIndex) => {
        const rowResult = (0, exports.getSanitizedRowData)({
            row,
            // excel/csv have a header row and are 1 based arrays, so adjust for our 0 based rowData
            rowIndex: rowIndex + STANDARD_ROW_INDEX_OFFSET,
            baseFieldConfigs,
            externalReferenceIdField,
            preSanitized,
        });
        if (rowResult.isValid) {
            if (markSanitized) {
                rowResult.data.finley_sanitized = true;
            }
            return rowResult.data;
        }
        fieldValidationErrors.push(rowResult.error);
        // return null for invalid rows,
        return null;
    });
    // if there are any errors, return failure with errors.
    if (fieldValidationErrors.length) {
        return {
            isValid: false,
            fieldValidationErrors,
        };
    }
    return {
        isValid: true,
        data: data.filter(common_1.typeguards.isNotNullOrUndefined),
    };
};
exports.getSanitizedCsvData = getSanitizedCsvData;
/**
 * Returns the type that a field will have after sanitization.
 * It also returns new name (if sanitizeFieldNames=true)
 */
const getSanitizedConfig = ({ configBeforeSanitize, sanitizeFieldNames, }) => {
    const bf = configBeforeSanitize;
    const field = (0, exports.getSanitizedFieldName)(configBeforeSanitize.field, sanitizeFieldNames);
    switch (bf.type) {
        // all these types resolve to float number
        case 'currency':
        case 'float':
        case 'percentage':
            return {
                field,
                type: 'float',
            };
        case 'optionalCurrency':
        case 'optionalFloat':
        case 'optionalPercentage':
            return {
                field,
                type: 'optionalFloat',
            };
        // booleans resolve to true as external
        case 'boolean':
        case 'optionalBoolean':
            return {
                field,
                type: bf.type,
                externalBooleanTrue: 'true',
            };
        // all these types resolve to the same type
        case 'integer':
        case 'string':
        case 'optionalInteger':
        case 'optionalString':
            return {
                field,
                type: bf.type,
            };
        // dates resolve to fulldatestr
        case 'date':
        case 'optionalDate':
            return {
                field,
                type: bf.type,
                externalDateFormat: 'yyyy-LL-dd',
            };
        // dateTimes resolve to iso dates
        case 'dateTime':
        case 'optionalDateTime':
            return {
                field,
                type: bf.type,
                externalDateTimeFormat: `yyyy-MM-dd'T'HH:mm:ss.SSS'Z'`,
                defaultTz: finley_types_1.Util.DateTimeZones.UTC,
            };
        default:
            return (0, common_1.finleyAssertUnreachable)(bf);
    }
};
exports.getSanitizedConfig = getSanitizedConfig;
const getSanitizeTransformOpOutputDataTape = (transformation) => {
    const baseFields = transformation.srcDataTape.baseFields.map((config) => (0, exports.getSanitizedConfig)({
        configBeforeSanitize: config,
        sanitizeFieldNames: transformation.sanitizeFieldNames,
    }));
    return {
        baseFields,
        delimiter: ',',
        sanitizedFieldNames: transformation.sanitizeFieldNames,
    };
};
exports.getSanitizeTransformOpOutputDataTape = getSanitizeTransformOpOutputDataTape;
const sanitizeTransformOp = async (input) => {
    try {
        const { srcDataTape, sanitizeFieldNames } = input.transformation;
        const baseFieldConfigs = (0, exports.createSanitizedFieldConfigs)({
            baseFields: srcDataTape.baseFields,
            sanitizeFieldNames,
        });
        /**
         * This here is a key component where we're actually mutating the CSV
         * even before the sanitization step during the buffer conversion process.
         * This leads to us having 3 steps throughout sanitization:
         * - Buffer: raw input
         * - Initial CSV: conversion from buffer with mutated headers
         * - Sanitized CSV: this now has sanitized values
         *
         * Due to this, we have to be mindful of how we're aligning the headers
         * during this flow since headers have already been mutated before
         * we call the actual value sanitization process
         */
        const { csvArray } = await common_1.csvConversion.finleyBufferToCsvArray({
            buffer: input.srcDataTapeBuffer,
            delimiter: srcDataTape.delimiter,
            headerRenameFunction: (rawHeader) => (0, exports.getSanitizedFieldName)(rawHeader, sanitizeFieldNames),
        });
        const csvResult = (0, exports.getSanitizedCsvData)({
            csvArray,
            baseFieldConfigs,
            preSanitized: false,
            // I am not sure why we do not include
            // finley_sanitized in this code path.
            // For now just keeping the existing behavior.
            markSanitized: false,
        });
        if (!csvResult.isValid) {
            return {
                isValid: false,
                errors: csvResult.fieldValidationErrors,
            };
        }
        const outputDataTape = (0, exports.getSanitizeTransformOpOutputDataTape)(input.transformation);
        const outputBuffer = (0, utils_1.rowDataToCsv)(csvResult.data, outputDataTape);
        return {
            isValid: true,
            data: { outputBuffer },
        };
    }
    catch (error) {
        return {
            isValid: false,
            errors: [
                {
                    // we are using metadataValidation for any unexpected errors
                    type: 'metadataValidation',
                    message: error instanceof Error
                        ? error.message
                        : 'Unexpected Error in sanitizeTransformOp',
                },
            ],
        };
    }
};
exports.sanitizeTransformOp = sanitizeTransformOp;
//# sourceMappingURL=helpers.js.map